/**
 * React Query mutation for AI text generation
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation } from '@tanstack/react-query';

import api, { getApiBaseUrl, getAuthToken, getCorrelationId } from '@/services/api';

import type {
  GenerateTextsPayload,
  GenerateTextsResponse,
  GenerateTextsBatchPayload,
  GenerateTextsBatchResponse,
} from '../types/campaign-texts.types';

/** Single generate call (incl. multi-country per-drop); avoids indefinite hangs. */
const SINGLE_GENERATE_TIMEOUT_MS = 180_000;

export function useGenerateTexts() {
  return useMutation<GenerateTextsResponse, Error, GenerateTextsPayload>({
    mutationFn: (payload) =>
      api
        .post('/campaign-texts/generate', payload, { timeout: SINGLE_GENERATE_TIMEOUT_MS })
        .then((r) => r.data.data),
  });
}

/** Batch generation can take 60–120s for many drops; use longer timeout. */
const BATCH_TIMEOUT_MS = 180_000;

type GenerateTextsBatchPayloadWithProgress = GenerateTextsBatchPayload & {
  onProgress?: (completed: number, total: number) => void;
};

export function useGenerateTextsBatch() {
  return useMutation<
    GenerateTextsBatchResponse,
    Error,
    GenerateTextsBatchPayloadWithProgress
  >({
    mutationFn: async ({ onProgress, ...payload }) => {
      if (!onProgress) {
        return api
          .post('/campaign-texts/generate-batch', payload, { timeout: BATCH_TIMEOUT_MS })
          .then((r) => r.data.data);
      }
      const base = getApiBaseUrl();
      const token = getAuthToken();
      const url = `${base}/api/campaign-texts/generate-batch-stream`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': getCorrelationId(),
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(BATCH_TIMEOUT_MS),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body?.getReader();
      if (!reader) throw new Error('No response body');
      const decoder = new TextDecoder();
      let buffer = '';
      let result: GenerateTextsBatchResponse | null = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.trim()) continue;
          const obj = JSON.parse(line) as { type: string; completed?: number; total?: number };
          if (obj.type === 'progress' && obj.completed != null && obj.total != null) {
            onProgress(obj.completed, obj.total);
          } else if (obj.type === 'done') {
            const { type: _, ...rest } = obj;
            result = rest as GenerateTextsBatchResponse;
          } else if (obj.type === 'error') {
            throw new Error((obj as { message?: string }).message ?? 'Unknown error');
          }
        }
      }
      if (buffer.trim()) {
        const obj = JSON.parse(buffer) as { type: string };
        if (obj.type === 'done') {
          const { type: _, ...rest } = obj;
          result = rest as GenerateTextsBatchResponse;
        } else if (obj.type === 'error') {
          throw new Error((obj as { message?: string }).message ?? 'Unknown error');
        }
      }
      if (!result) throw new Error('No result from stream');
      return result;
    },
  });
}
